import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import { IconEye, IconEdit, IconDownload } from '@nxsys/components/AppIconSvg';
import { defaultDocumentTypeOptions } from '@nxsys/constants/AppConstants';
import { ColumnsType } from 'antd/es/table';
import {
  DocumentType,
  GetDocumentParams,
  DocumentPayload,
} from '@nxsys/types/models/document';
import AppCard from '@nxsys/components/AppCard';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTable from '@nxsys/components/AppTableContainer';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppsDeleteIcon from '@nxsys/components/AppsDeleteIcon';
import AppAttachment from '@nxsys/components/AppAttachment';
import DocumentHeader from '../DocumentHeader';
import CreateModal from './CreateModal';
import UpdateModal from './UpdateModal';
import DetailModal from './DetailModal';
import { useSelector } from 'react-redux';
import type { AppState } from '@nxsys/store';

type Props = {
  data: {
    list: DocumentType[];
    pagination: { current: number; pageSize: number; total: number };
    queryParams: GetDocumentParams | null;
  };
  loading: boolean;
  onGetDocuments: (params: GetDocumentParams, key?: string) => void;
  onDeleteDocument: (id: string, cb?: () => void) => void;
  onChangeSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCreatedDocument: (params: DocumentPayload) => void;
  onUpdateDocument: (
    documentId: string,
    params: DocumentPayload,
    cb?: (document: DocumentType) => void,
  ) => void;
  addNewDefaultValues?: any;
  listDisabled?: string[];
};

const DocumentAgency = ({
  data,
  loading,
  onGetDocuments,
  onDeleteDocument,
  onChangeSearchTerm,
  onCreatedDocument,
  onUpdateDocument,
  addNewDefaultValues,
  listDisabled = [],
}: Props) => {
  const { messages } = useIntl();
  const { isAgency } = useSelector((state: AppState) => state.appStore);
  const {
    list,
    pagination: { total, pageSize, current: page },
    queryParams,
  } = data;
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [documentSelected, setDocumentSelected] = useState<DocumentType | null>(
    null,
  );

  const onToggleCreateDocument = () => {
    setIsOpenCreate(!isOpenCreate);
  };

  const onToggleUpdateDocument = (document?: DocumentType) => {
    setIsOpenUpdate(!isOpenUpdate);
    setDocumentSelected(document || null);
  };

  const onToggleDetailDocument = (document?: DocumentType) => {
    console.log('onToggleDetailDocument', document);
    setIsOpenDetail(!isOpenDetail);
    setDocumentSelected(document || null);
  };

  const onChangePagination = (page: number, pageSize: number) => {
    onGetDocuments({
      page: total > pageSize ? page - 1 : 0,
      size: pageSize,
    });
  };

  const handleFilterAgency = (params: GetDocumentParams) => {
    onGetDocuments({
      ...(queryParams ?? { page: 0, size: 20 }),
      ...params,
      page: 0,
      size: queryParams?.size ?? 20,
    });
  };
  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      onGetDocuments({
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      onGetDocuments({
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const columns: ColumnsType<DocumentType> = [
    {
      key: 'actions',
      title: 'Actions',
      fixed: 'left',
      align: 'left',
      width: 200,
      render: (record) => {
        if (isAgency) {
          return (
            <ul className="table-actions flex items-center justify-start">
              <li>
                <AppIconButton
                  title="View"
                  icon={<IconEye />}
                  onClick={onToggleDetailDocument.bind(this, record)}
                />
              </li>
              <li>
                <AppIconButton
                  title="Download"
                  icon={<IconDownload disabled={!record?.documentFile} />}
                  disabled={!record?.documentFile}
                  onClick={() => window.open(record?.documentFile, '_blank')}
                />
              </li>
            </ul>
          );
        }
        return (
          <ul className="table-actions flex items-center justify-start">
            <li>
              <AppIconButton
                title="View"
                icon={<IconEye />}
                onClick={onToggleDetailDocument.bind(this, record)}
              />
            </li>
            <li>
              <AppIconButton
                title="Edit"
                icon={<IconEdit />}
                onClick={onToggleUpdateDocument.bind(this, record)}
              />
            </li>
            <li>
              <AppIconButton
                title="Download"
                icon={<IconDownload disabled={!record?.documentFile} />}
                disabled={!record?.documentFile}
                onClick={() => window.open(record?.documentFile, '_blank')}
              />
            </li>
            <li>
              <AppsDeleteIcon
                deleteAction={() => onDeleteDocument(record?.id)}
                title="Delete Document"
                paragraph={
                  <p>
                    Are you sure want to delete <a>"{record.id}"</a> document?
                  </p>
                }
              />
            </li>
          </ul>
        );
      },
    },
    {
      key: 'id',
      title: 'Doc ID',
      fixed: 'left',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'agencyRef',
      title: 'Agency Name',
      // dataIndex: 'agencyRef',
      render: (record) => {
        const author =
          record?.agencyRef || record?.prospectsOnboardingRef?.agency;
        return <span>{author?.name || '_'}</span>;
      },
    },
    {
      key: 'createdAt',
      title: 'Received On',
      dataIndex: 'createdAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : ''}
        </span>
      ),
    },
    {
      key: 'documentDate',
      title: 'Document Date',
      dataIndex: 'documentDate',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : ''}
        </span>
      ),
    },
    {
      key: 'documentType',
      title: 'Document Type',
      dataIndex: 'documentType',
      align: 'center',
      render: (value) =>
        defaultDocumentTypeOptions.find((item) => item?.value === value)
          ?.label || '_',
    },
    {
      key: 'documentFile',
      title: 'Attachment',
      width: 300,
      render: ({ documentFile, documentTitle }) =>
        documentFile ? (
          <AppAttachment urlFile={documentFile} title={documentTitle} />
        ) : (
          '_'
        ),
    },
    {
      key: 'documentTitle',
      title: 'Document Title',
      dataIndex: 'documentTitle',
      render: (value) => value || '_',
    },
    {
      key: 'expiryDate',
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : ''}
        </span>
      ),
    },
  ];

  return (
    <AppCard>
      <AppsHeader>
        <DocumentHeader
          isCreate
          isFilterStatus={isAgency}
          onChangeSearch={onChangeSearchTerm}
          onToggleCreate={onToggleCreateDocument}
          onFilterAgency={handleFilterAgency}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          total={total}
          data={list || []}
          loading={loading}
          page={page}
          pageSize={pageSize}
          columns={columns}
          titleEmpty={messages['documents.empty'] as string}
          onChangeTable={onChangeTable}
          onChangePagination={onChangePagination}
          noDataType={!!queryParams?.search ? 'noDataFound' : 'noDataCreated'}
          addNewBtnOnClick={onToggleCreateDocument}
        />
      </AppsContent>
      <CreateModal
        title="Add New Document"
        isOpen={isOpenCreate}
        toggle={onToggleCreateDocument}
        onCreatedDocument={onCreatedDocument}
        defaultValues={addNewDefaultValues}
        listDisabled={listDisabled}
      />
      <UpdateModal
        title="Edit Document"
        isOpen={isOpenUpdate}
        documentSelected={documentSelected}
        toggle={onToggleUpdateDocument}
        onUpdateDocument={onUpdateDocument}
        listDisabled={listDisabled}
      />
      <DetailModal
        title="Document Detail"
        isOpen={isOpenDetail}
        documentSelected={documentSelected}
        toggle={onToggleDetailDocument}
        onDeleteDocument={onDeleteDocument}
        toggleUpdate={onToggleUpdateDocument}
      />
    </AppCard>
  );
};

export default DocumentAgency;
